import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Preview as PreviewIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyId } from '../../hooks/useCompanyId';
import { CompanyLayout } from '../../components/Layout';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { useNavigate } from 'react-router-dom';
import { CompanyProfileForm } from '../../components/CompanyProfile/CompanyProfileForm';
import { CompanyProfileDisplay } from '../../components/CompanyProfile/CompanyProfileDisplay';
import { UpdateCompanyProfileRequest } from '../../types/company-profile';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-profile-tabpanel-${index}`}
      aria-labelledby={`company-profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const CompanyProfilePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const [currentTab, setCurrentTab] = useState(0);

  // 管理者は管理画面にリダイレクト
  useEffect(() => {
    if (user?.userRole === 'admin') {
      navigate('/admin/management/companies');
    }
  }, [user?.userRole, navigate]);

  const {
    company,
    profile,
    permissions,
    isLoading,
    isSaving,
    error,
    validationErrors,
    updateProfile,
    clearValidationErrors,
  } = useCompanyProfile(companyId);

  // 権限チェック（企業ユーザーのみ）
  if (!user || !['company_admin', 'hr', 'employee'].includes(user.userRole)) {
    return (
      <CompanyLayout>
        <Container
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              アクセス権限がありません
            </Typography>
            <Typography variant="body2">
              企業プロフィール管理は企業ユーザーのみ利用できます。
              {user?.userRole === 'admin' && (
                <>
                  <br />
                  管理者の方は管理画面をご利用ください。
                </>
              )}
            </Typography>
          </Alert>
        </Container>
      </CompanyLayout>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('Tab change requested:', { from: currentTab, to: newValue });
    setCurrentTab(newValue);
    clearValidationErrors();
  };

  const handleUpdateProfile = async (data: UpdateCompanyProfileRequest) => {
    const result = await updateProfile(data);
    // 更新成功時は表示タブに切り替え（オプション）
    // ユーザーが編集を続けたい場合もあるので、自動切り替えは削除
    // if (!error && Object.keys(validationErrors).length === 0) {
    //   setCurrentTab(1);
    // }
  };

  if (companyIdLoading || isLoading) {
    return (
      <CompanyLayout>
        <Container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress size={40} />
        </Container>
      </CompanyLayout>
    );
  }

  if (!companyId) {
    return (
      <CompanyLayout>
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">
            企業情報を取得できませんでした。管理者にお問い合わせください。
          </Alert>
        </Container>
      </CompanyLayout>
    );
  }

  if (error && !company) {
    return (
      <CompanyLayout>
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container maxWidth="xl">
        {/* ページヘッダー */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 1,
                color: '#111827',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <BusinessIcon sx={{ mr: 1 }} />
              企業プロフィール管理
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              企業ページの作成・編集
            </Typography>
          </Box>
          {company && (
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              {company.name}
            </Typography>
          )}
        </Box>

        {/* タブナビゲーション */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<EditIcon />} label={isSaving ? '保存中...' : '編集'} />
            <Tab
              icon={<PreviewIcon />}
              label="プレビュー"
              disabled={!company || !profile}
            />
          </Tabs>
        </Paper>

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* タブコンテンツ */}
        <TabPanel value={currentTab} index={0}>
          {/* 編集フォーム */}
          {company && profile && (
            <>
              {!permissions?.can_edit && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  編集権限がありません。企業管理者・人事担当者のみプロフィールを編集できます。
                </Alert>
              )}
              <CompanyProfileForm
                companyId={companyId}
                initialData={{
                  business_content: profile.business_content,
                  recruiting_message: profile.recruiting_message,
                  job_types: profile.job_types,
                  target_candidates: profile.target_candidates,
                  company_images: profile.company_images,
                  is_published: profile.is_published,
                  logo_url: profile.logo_url || company.logo_url,
                  employee_count_range: company.employee_count_range,
                }}
                onSubmit={handleUpdateProfile}
                isSaving={isSaving}
                validationErrors={validationErrors}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {/* プレビュー表示 */}
          {company && profile && (
            <CompanyProfileDisplay
              company={company}
              profile={profile}
              variant="management"
            />
          )}
        </TabPanel>
      </Container>
    </CompanyLayout>
  );
};
